import { Empty, Flex } from 'antd';
import invariant from 'tiny-invariant';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import graphContainerStyles from 'business/layout/single-card-page-with-filter-card/graph.module.scss';
import AsideActivityTree from 'business/shift-report/report-analyze/pages/activity-analyze/components/aside-activity-tree';
import { ActivityTreeMetadata } from 'business/shift-report/report-analyze/pages/activity-analyze/components/aside-activity-tree/use-activity-tree';
import ReportActivityAnalyzeGraph from 'business/shift-report/report-analyze/pages/activity-analyze/components/graph';
import ReportActivityAnalyzeTable from 'business/shift-report/report-analyze/pages/activity-analyze/components/table';
import {
  useReportActivityAnalyzeActivities,
  useReportActivityAnalyzeFilters,
} from 'business/shift-report/report-analyze/pages/activity-analyze/providers/filters';
import {
  ACTIVITY_DISPLAY_TYPES_MAP,
  ACTIVITY_GROUP_BY_TYPES_MAP,
} from 'business/shift-report/report-analyze/pages/activity-analyze/types';
import { Module_Enum } from 'generated/graphql';
import { useMediaType } from 'technical/media/hooks';
import { getFetchRange } from 'technical/range-filters/get-fetch-range';
import { SimpleLoader } from 'ui/loader';

import { formatData } from './format-data';
import styles from './index.module.scss';
import { useActivityAnalyzeV1ControllerReportActivityAnalyzeQuery } from './use-activity-analyze';

type ReportActivityAnalyzeDisplayContainerProps = {
  activityTreeMetadata: ActivityTreeMetadata;
  showTable?: boolean;
};

const ReportActivityAnalyzeDisplayContainer = ({
  activityTreeMetadata,
  showTable = false,
}: ReportActivityAnalyzeDisplayContainerProps) => {
  const { isMobile } = useMediaType();
  const { currentConstructionSiteId } = useAppContext();
  const headers = useRestApiHeaders(Module_Enum.ShiftReport);
  invariant(currentConstructionSiteId, 'No constructionSite id');

  const filters = useReportActivityAnalyzeFilters();
  const checkedKeys = useReportActivityAnalyzeActivities();
  const fetchRange = getFetchRange(filters);

  const { data, isLoading, isError } =
    useActivityAnalyzeV1ControllerReportActivityAnalyzeQuery(
      {
        body: {
          constructionSiteId: currentConstructionSiteId,
          ...fetchRange,
          groupByType:
            filters.displayType === ACTIVITY_DISPLAY_TYPES_MAP.pie
              ? ACTIVITY_GROUP_BY_TYPES_MAP.activity
              : filters.groupByType,
          aggregationLevel: filters.aggregationLevel,
          criticalType: filters.criticalType,
          selectedActivityId: checkedKeys,
        },
        headers,
      },
      {
        enabled: checkedKeys.length > 0,
      },
    );

  const formatedData = formatData(data?.graphData, filters);

  if (isError) {
    return (
      <Flex className={styles.container}>
        {!isMobile ? <div className={styles.aside} /> : null}
        <div className={graphContainerStyles.graphContainer}>
          <Flex justify="center" align="center">
            <Empty />
          </Flex>
        </div>
      </Flex>
    );
  }

  if (isLoading) {
    return (
      <Flex className={styles.container}>
        {!isMobile ? <div className={styles.aside} /> : null}
        <div className={graphContainerStyles.graphContainer}>
          <SimpleLoader />
        </div>
      </Flex>
    );
  }

  if (filters.displayType === ACTIVITY_DISPLAY_TYPES_MAP.table || showTable) {
    if (data?.totalLine) {
      formatedData.push(data.totalLine);
    }

    return (
      <Flex className={styles.container}>
        {!isMobile ? (
          <div className={styles.aside}>
            <AsideActivityTree metadata={activityTreeMetadata} />
          </div>
        ) : null}
        <ReportActivityAnalyzeTable
          reportActivityAnalyzeGraphData={formatedData}
          reportActivitySeries={data?.activitySeries ?? []}
        />
      </Flex>
    );
  }

  return (
    <Flex className={styles.container}>
      {!isMobile ? (
        <div className={styles.aside}>
          <AsideActivityTree metadata={activityTreeMetadata} />
        </div>
      ) : null}
      <div className={graphContainerStyles.graphContainer}>
        <ReportActivityAnalyzeGraph
          graphType={filters.displayType}
          reportActivityAnalyzeGraphData={formatedData}
          reportActivitySeries={data?.activitySeries ?? []}
        />
      </div>
    </Flex>
  );
};

export default ReportActivityAnalyzeDisplayContainer;
